import React from 'react';
import {
    BarChart3,
    LineChart as LineChartIcon,
    PieChart as PieChartIcon,
    AreaChart as AreaChartIcon,
    Table2,
    Layers,
    BarChartHorizontal,
    Gauge,
} from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export type VizType =
    | 'bar'
    | 'line'
    | 'area'
    | 'pie'
    | 'stacked-bar'
    | 'horizontal-bar'
    | 'gauge'
    | 'table';

interface VizOption {
    type: VizType;
    labelKey: string;
    labelDefault: string;
    icon: React.ReactNode;
}

const ALL_VIZ_OPTIONS: VizOption[] = [
    { type: 'bar', labelKey: 'manager.modules.claudeQuery.vizSwitcher.bar', labelDefault: 'Stolpe', icon: <BarChart3 size={13} /> },
    { type: 'line', labelKey: 'manager.modules.claudeQuery.vizSwitcher.line', labelDefault: 'Linje', icon: <LineChartIcon size={13} /> },
    { type: 'area', labelKey: 'manager.modules.claudeQuery.vizSwitcher.area', labelDefault: 'Område', icon: <AreaChartIcon size={13} /> },
    { type: 'pie', labelKey: 'manager.modules.claudeQuery.vizSwitcher.pie', labelDefault: 'Kake', icon: <PieChartIcon size={13} /> },
    { type: 'stacked-bar', labelKey: 'manager.modules.claudeQuery.vizSwitcher.stacked', labelDefault: 'Stablet', icon: <Layers size={13} /> },
    { type: 'horizontal-bar', labelKey: 'manager.modules.claudeQuery.vizSwitcher.horizontal', labelDefault: 'Horisontal', icon: <BarChartHorizontal size={13} /> },
    { type: 'gauge', labelKey: 'manager.modules.claudeQuery.vizSwitcher.gauge', labelDefault: 'Gauge', icon: <Gauge size={13} /> },
    { type: 'table', labelKey: 'manager.modules.claudeQuery.vizSwitcher.table', labelDefault: 'Tabell', icon: <Table2 size={13} /> },
];

/** Default chart types shown when switching FROM a chart */
const CHART_SOURCE_TYPES: VizType[] = ['bar', 'line', 'area', 'pie', 'stacked-bar', 'horizontal-bar', 'table'];

/** Chart types shown when switching FROM a table */
const TABLE_SOURCE_TYPES: VizType[] = ['bar', 'line', 'area', 'pie', 'table'];

interface VizSwitcherProps {
    /** Currently active visualization type */
    currentType: VizType;
    /** Callback when user selects a different type */
    onSwitch: (type: VizType) => void;
    /** Optionally restrict which types are available */
    allowedTypes?: VizType[];
    /** Source block type — used to determine default allowed types */
    sourceBlockType?: 'chart' | 'table';
    /** Bare ikoner (smale paneler i Compass-visninger) */
    iconOnly?: boolean;
}

export const VizSwitcher: React.FC<VizSwitcherProps> = ({
    currentType,
    onSwitch,
    allowedTypes,
    sourceBlockType = 'chart',
    iconOnly = false,
}) => {
    const { t } = useTranslation();
    const types = allowedTypes
        || (sourceBlockType === 'table' ? TABLE_SOURCE_TYPES : CHART_SOURCE_TYPES);

    const options = ALL_VIZ_OPTIONS.filter(opt => types.includes(opt.type));

    return (
        <div className="flex items-center gap-0.5 bg-gray-50 border border-gray-200 rounded-md p-0.5">
            {options.map(opt => {
                const isActive = opt.type === currentType;
                const label = t(opt.labelKey, {}, opt.labelDefault);
                return (
                    <button
                        key={opt.type}
                        onClick={() => onSwitch(opt.type)}
                        title={label}
                        aria-label={label}
                        className={`
                            flex items-center gap-1 rounded text-xs transition-colors
                            ${iconOnly ? 'justify-center min-w-[44px] min-h-[44px]' : 'px-1.5 py-1'}
                            ${isActive
                                ? 'bg-white shadow-sm text-blue-600 font-medium border border-gray-200'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-transparent'
                            }
                        `}
                    >
                        {opt.icon}
                        {!iconOnly && <span className="hidden sm:inline">{label}</span>}
                    </button>
                );
            })}
        </div>
    );
};
